// Vendor Connection v1 — the explicit two-sided vendor↔customer connection.
// Buyer side ("My Vendors"): connected vendors with their goals + §1/§2
// report cards + the buyer's own ratings and private notes. Vendor side
// ("Customers"): minimal contact cards (name / business / industry / email /
// phone — nothing else by design), per-loan work view, engagement status,
// private notes, and the one signed-contract slot per connection. All
// scoping is server-side off the authenticated account — these payloads
// never carry the other party's private notes.

import { portalApi } from "./client";
import type {
  BorrowerGoalDto,
  BorrowerVendorReportDto,
  BorrowerSignOffSummaryDto,
} from "./borrower";

export type EngagementStatus =
  | "new"
  | "contacted"
  | "in-negotiation"
  | "under-contract";

export const ENGAGEMENT_STATUS_LABEL: Record<EngagementStatus, string> = {
  "new": "New",
  "contacted": "Contacted",
  "in-negotiation": "In negotiation",
  "under-contract": "Under contract",
};

// ── Buyer side ───────────────────────────────────────────────────────────

export interface BorrowerConnectedVendorLoanDto {
  connectionId: string;
  loanId: string;
  loanLabel: string;
  roleOnLoan: string;
  connectedAt: string;
  /** Read-only on the buyer side (vendor/staff drive it). */
  engagementStatus: EngagementStatus;
  isNew: boolean;
  /** The buyer's OWN private notes — never visible to the vendor. */
  notes: string | null;
  goals: BorrowerGoalDto[];
  reports: BorrowerVendorReportDto[];
}

export interface BorrowerConnectedVendorDto {
  vendorId: string;
  vendorName: string;
  vendorType: string | null;
  loans: BorrowerConnectedVendorLoanDto[];
}

export const portalBuyerConnectionsApi = {
  list: () => portalApi.get<BorrowerConnectedVendorDto[]>("/buyer/vendors"),
  newCount: () => portalApi.get<number>("/buyer/vendors/new-count"),
  markViewed: (connectionId: string) =>
    portalApi.post<boolean>(`/buyer/vendors/${connectionId}/viewed`),
  updateNotes: (connectionId: string, notes: string | null) =>
    portalApi.put<BorrowerConnectedVendorLoanDto>(
      `/buyer/vendors/${connectionId}/notes`,
      { notes },
    ),
};

// ── Vendor side ──────────────────────────────────────────────────────────

export interface VendorCustomerContactDto {
  buyerName: string | null;
  businessName: string | null;
  industry: string | null;
  email: string | null;
  phone: string | null;
}

export interface VendorCustomerGoalDto {
  goalId: string;
  title: string;
  status: string;
  completionCriteria: string | null;
  targetMonths: string | null;
}

export interface VendorCustomerReportGradeDto {
  goalId: string;
  goalTitle: string;
  score: number;
  isComplete: boolean;
  note: string | null;
}

export interface VendorCustomerReportDto {
  reportId: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  publicNarrative: string | null;
  goalGrades: VendorCustomerReportGradeDto[];
  signOff: BorrowerSignOffSummaryDto | null;
}

export interface VendorCustomerContractDto {
  documentId: string;
  fileName: string;
  version: number;
  uploadedAt: string;
}

export interface VendorCustomerLoanSectionDto {
  connectionId: string;
  loanId: string;
  loanLabel: string;
  roleOnLoan: string;
  connectedAt: string;
  engagementStatus: EngagementStatus;
  isNew: boolean;
  /** The vendor's OWN private notes — never visible to the buyer. */
  notes: string | null;
  contract: VendorCustomerContractDto | null;
  goals: VendorCustomerGoalDto[];
  reports: VendorCustomerReportDto[];
}

export interface VendorCustomerDto {
  customerKey: string;
  contact: VendorCustomerContactDto;
  loans: VendorCustomerLoanSectionDto[];
}

export interface DocumentDownloadDto {
  sasUrl: string;
  expiresAt: string;
  isPdf: boolean;
}

export const portalVendorCustomersApi = {
  list: () => portalApi.get<VendorCustomerDto[]>("/vendor/customers"),
  newCount: () => portalApi.get<number>("/vendor/customers/new-count"),
  markViewed: (connectionId: string) =>
    portalApi.post<boolean>(`/vendor/customers/${connectionId}/viewed`),
  updateEngagementStatus: (connectionId: string, status: EngagementStatus) =>
    portalApi.patch<VendorCustomerLoanSectionDto>(
      `/vendor/customers/${connectionId}/engagement-status`,
      { status },
    ),
  updateNotes: (connectionId: string, notes: string | null) =>
    portalApi.put<VendorCustomerLoanSectionDto>(
      `/vendor/customers/${connectionId}/notes`,
      { notes },
    ),
  uploadContract: (connectionId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return portalApi.upload<VendorCustomerLoanSectionDto>(
      `/vendor/customers/${connectionId}/contract`,
      form,
    );
  },
  contractDownloadUrl: (connectionId: string) =>
    portalApi.get<DocumentDownloadDto>(
      `/vendor/customers/${connectionId}/contract-download-url`,
    ),
};
